/* From Uiverse.io by Smit-Prajapati */
import { useAccount, useContractWrite, useWaitForTransaction } from "wagmi";
import { useAppSelector } from "../../app/hooks";
import "./confirmBtn.css";
import { useEffect, useState } from "react";
import { selectChain } from "../../features/chain";
import { createPublicClient, http } from "viem";
import { Chains, SpaceAcceptInviteCodeABI, SpaceHigherAddrABI } from "../data";
import toast from "react-hot-toast";
import { emptyEthAddress } from "../../utils/util";
import { resolveJicReferralCode } from "../../lib/jicReferral";
import {
  getInjectedWalletAddress,
  sendInjectedContractTransaction,
  waitForInjectedReceipt,
} from "../../lib/injectedWallet";
export type ConfirmBtnProps = {
  refreshHigherAddr: any;
};
function normalizeInviteErrorMessage(message: string) {
  return /user.*reject|rejected.*request|user.*denied|denied.*request|cancelled|canceled/i.test(message)
    ? "Request rejected in wallet"
    : message;
}

// 用实际签名地址查链上 higherAddress；查询失败时返回 ""（放行，由合约兜底）
async function readHigherAddressOnChain(
  rpcUrl: string,
  contractAddr: `0x${string}`,
  account: `0x${string}`
) {
  try {
    const client = createPublicClient({ transport: http(rpcUrl) });
    const higher = await client.readContract({
      address: contractAddr,
      abi: SpaceHigherAddrABI,
      functionName: "higherAddress",
      args: [account],
    });
    return typeof higher === "string" ? higher : "";
  } catch {
    return "";
  }
}

const ConfirmBtn = ({ refreshHigherAddr }: ConfirmBtnProps) => {
  const chain = useAppSelector(selectChain);
  const { address: signerAddress } = useAccount();
  const [code, setCode] = useState("");
  const [processing, setProcessing] = useState(false);
  useEffect(() => {
    let live = true;
    resolveJicReferralCode("face").then((codeInQuery) => {
      if (live && codeInQuery) setCode(codeInQuery);
    });
    return () => {
      live = false;
    };
  }, []);
  const handleAcceptInviteError = (error: Error) => {
    var simpleMsg = normalizeInviteErrorMessage(error.message.split(".")[0]);
    if (error.message.includes("Couldn't find the inviter")) {
      simpleMsg = "Couldn't find the inviter, please check invitation code";
    } else if (error.message.includes("You already have an inviter")) {
      simpleMsg = "You already have an inviter, please refresh the page";
    }
    toast.error(simpleMsg);
    setProcessing(false);
    refreshHigherAddr();
  };

  const { data: acceptInviteCodeRes, write: accpetInviteCode } =
    useContractWrite({
      address: Chains[chain].contractAddr as `0x${string}`,
      abi: SpaceAcceptInviteCodeABI,
      functionName: "acceptInvite",
      args: [code],
      onError: handleAcceptInviteError,
    });

  useWaitForTransaction({
    hash: acceptInviteCodeRes?.hash,
    onSuccess() {
      toast.success("Accept invitation succeeded!");
      setProcessing(false);
      refreshHigherAddr();
    },
    onError(err) {
      var simpleMsg = normalizeInviteErrorMessage("Accept code failed!" + err.message.split(".")[0]);
      toast.error(simpleMsg);
      setProcessing(false);
      refreshHigherAddr();
    },
  });

  const onConfirmInvite = async () => {
    if (!code || code.trim() === "") {
      toast.error("Invitation code cannot be empty", {
        id: "invitation-code-empty",
      });
      return;
    }

    setProcessing(true);
    // 发交易前先用实际签名地址检查是否已绑定邀请人，避免推送一笔注定 revert 的交易
    const senderAddress =
      signerAddress ?? (await getInjectedWalletAddress().catch(() => undefined));
    if (senderAddress) {
      const higher = await readHigherAddressOnChain(
        Chains[chain].provider,
        Chains[chain].contractAddr as `0x${string}`,
        senderAddress as `0x${string}`
      );
      if (higher && higher !== emptyEthAddress) {
        toast.success("Invitation already applied", {
          id: "invitation-already-applied",
        });
        setProcessing(false);
        refreshHigherAddr();
        return;
      }
    }
    if (signerAddress && accpetInviteCode) {
      accpetInviteCode();
      return;
    }

    try {
      const txHash = await sendInjectedContractTransaction({
        chainId: Chains[chain].chainId,
        chainName: Chains[chain].chainName,
        rpcUrl: Chains[chain].provider,
        to: Chains[chain].contractAddr as `0x${string}`,
        abi: SpaceAcceptInviteCodeABI,
        functionName: "acceptInvite",
        args: [code],
      });
      await waitForInjectedReceipt(txHash, {
        rpcUrl: Chains[chain].provider,
      });
      toast.success("Accept invitation succeeded!");
      refreshHigherAddr();
    } catch (error) {
      handleAcceptInviteError(
        error instanceof Error ? error : new Error("Accept code failed")
      );
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="w-full flex flex-col">
      <p className="p-4 font-Armstrong text-lg md:text-xl text-gray-300">
        Who invited me?
      </p>
      <div className=" w-full input-container">
        <input
          type="text"
          placeholder="Invitation Code"
          value={code}
          onChange={(e: any) => setCode(e.target.value.trim().toLowerCase())}
        />
        <button
          className="button"
          onClick={onConfirmInvite}
          disabled={processing}
        >
          {processing ? "Processing" : "Confirm"}
        </button>
      </div>
    </div>
  );
};
export default ConfirmBtn;
